import { SearchOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Input,
  Layout,
  Select,
  Space,
  Table,
} from "antd";
import { Content } from "antd/es/layout/layout";
import { ColumnType } from "antd/es/table/interface";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import Loader from "../../../Home/Loader/Loader";
import noResult from "../../../Utility/images/noresult.png";
import { getBOMDetails } from "../../../redux/actions/API/bomDetails";
import { useNavigate, Link } from "react-router-dom";
import { getUsers } from "../../../redux/actions/API/users";

interface IDropdownOption {
  label: string;
  value: string | number;
}

interface IProductionPayload {
  id: number;
  companyId: number;
  productionId: string;
  documentNumber: string | null;
  bomId: number | null;
  productionEndDate: string | null;
  assignedTo: number | null;
  status: number;
  createdAt: string;
  updatedAt: string;
  mto: any;
  [key: string]: any;
}

interface IProductionExtraProps {
  breadcrumbTitle: string;
  loading: boolean;
  tableData:
    | IProductionPayload[]
    | {
        data?: IProductionPayload[];
        items?: IProductionPayload[];
        total?: number;
      };
  dropdownOptions: IDropdownOption[];
  selectedValue?: string | number;
  handleDropdownChange?: Function;
  usersData: any;
  bomDetailsData: any;
  getBOMDetails: Function;
  bomDetailsLoading: boolean;
  companyId: any;
  getUsers: Function;
}

interface ProductionRowData extends IProductionPayload {
  key: string | number;
}

type SearchableColumnKey =
  | "productionId"
  | "documentNumber"
  | "bomId"
  | "productionEndDate"
  | "productionStartDate"
  | "productionType"
  | "productionCompletionDate"
  | "assignedTo";

const ProductionExtra: React.FC<IProductionExtraProps> = ({
  breadcrumbTitle,
  loading,
  tableData,
  dropdownOptions,
  selectedValue,
  handleDropdownChange,
  usersData,
  bomDetailsData,
  getBOMDetails,
  bomDetailsLoading,
  companyId,
  getUsers,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [globalSearchText, setGlobalSearchText] = useState("");
  const [columnSearchText, setColumnSearchText] = useState<
    Record<SearchableColumnKey, string>
  >({
    productionId: "",
    documentNumber: "",
    bomId: "",
    productionEndDate: "",
    productionStartDate: "",
    productionType: "",
    productionCompletionDate: "",
    assignedTo: "",
  });

  const navigate = useNavigate();

  const normalizedOptions: IDropdownOption[] = Array.isArray(dropdownOptions)
    ? dropdownOptions
        .map((option: any) => ({
          label: String(option?.label ?? option?.name ?? ""),
          value: option?.value ?? option?.id,
        }))
        .filter(
          (option: IDropdownOption) =>
            option.label && option.value !== undefined,
        )
    : [];

  const usersMap = useMemo(() => {
    if (!Array.isArray(usersData)) return {};

    return usersData.reduce((acc: Record<number, string>, user: any) => {
      if (typeof user?.id === "number") {
        acc[user.id] = user?.name || "";
      }
      return acc;
    }, {});
  }, [usersData]);

  const getDisplayedProductionId = (record: ProductionRowData): string => {
    return String(
      record?.production?.productionId || record?.productionId || "",
    ).toLowerCase();
  };

  const getDisplayedBomId = (record: ProductionRowData): string => {
    const bomId = record.bomId;
    if (bomId === null || bomId === undefined) return "n/a";
    const bom = (Array.isArray(bomDetailsData) ? bomDetailsData : [])?.find(
      (b: any) => b.id === bomId,
    );
    return String(bom?.bomId || "N/A").toLowerCase();
  };

  const getDisplayedProductionType = (record: ProductionRowData): string => {
    if (record?.mto === 0) return "mts";
    if (record?.mto === 1) return "mto";
    return "n/a";
  };

  const getDisplayedStartDate = (record: ProductionRowData): string => {
    return String(
      record?.production?.productionStartDate?.slice(0, 10) ||
        record?.productionStartDate?.slice(0, 10) ||
        "",
    ).toLowerCase();
  };

  const getDisplayedCompletionDate = (record: ProductionRowData): string => {
    return String(
      record?.production?.productionCompletionDate?.slice(0, 10) ||
        record?.productionCompletionDate?.slice(0, 10) ||
        "",
    ).toLowerCase();
  };

  const getDisplayedAssignedTo = (record: ProductionRowData): string => {
    if (record.assignedTo === null || record.assignedTo === undefined)
      return "n/a";
    return String(usersMap[record.assignedTo] || "N/A").toLowerCase();
  };

  const getColumnValueForSearch = (
    record: ProductionRowData,
    columnKey: SearchableColumnKey,
  ): string => {
    if (columnKey === "productionId") return getDisplayedProductionId(record);
    if (columnKey === "documentNumber")
      return String(record.documentNumber || "N/A").toLowerCase();
    if (columnKey === "bomId") return getDisplayedBomId(record);
    if (columnKey === "productionEndDate")
      return String(record.productionEndDate || "N/A").toLowerCase();
    if (columnKey === "productionStartDate")
      return getDisplayedStartDate(record);
    if (columnKey === "productionType")
      return getDisplayedProductionType(record);
    if (columnKey === "productionCompletionDate")
      return getDisplayedCompletionDate(record);
    if (columnKey === "assignedTo") return getDisplayedAssignedTo(record);
    return "";
  };

  const rawData = useMemo<IProductionPayload[]>(() => {
    if (Array.isArray(tableData)) return tableData;
    if (Array.isArray(tableData?.data)) return tableData.data;
    if (Array.isArray(tableData?.items)) return tableData.items;
    return [];
  }, [tableData]);

  const mappedData: ProductionRowData[] = rawData.map(
    (item: IProductionPayload, index: number) => ({
      ...item,
      key: item.id ?? index,
      productionId: String(item.productionId || ""),
      documentNumber: item.documentNumber || null,
      bomId: item.bomId ?? null,
      productionEndDate: item.productionEndDate || null,
      assignedTo: item.assignedTo ?? null,
    }),
  );

  const globallyFilteredData = useMemo(() => {
    const normalizedSearch = globalSearchText.trim().toLowerCase();
    if (!normalizedSearch) return mappedData;

    return mappedData.filter((record) => {
      const searchableColumns: SearchableColumnKey[] = [
        "productionId",
        "documentNumber",
        "bomId",
        "productionEndDate",
        "productionStartDate",
        "productionType",
        "productionCompletionDate",
        "assignedTo",
      ];

      return searchableColumns.some((columnKey) =>
        getColumnValueForSearch(record, columnKey).includes(normalizedSearch),
      );
    });
  }, [mappedData, globalSearchText, bomDetailsData, usersMap]);

  const fullyFilteredData = useMemo(() => {
    return globallyFilteredData.filter((record) => {
      const searchableColumns = Object.keys(
        columnSearchText,
      ) as SearchableColumnKey[];

      return searchableColumns.every((columnKey) => {
        const searchText = columnSearchText[columnKey].trim().toLowerCase();
        if (!searchText) return true;
        return getColumnValueForSearch(record, columnKey).includes(searchText);
      });
    });
  }, [globallyFilteredData, columnSearchText, bomDetailsData, usersMap]);

  const filteredTotal = fullyFilteredData.length;

  useEffect(() => {
    getBOMDetails({
      companyId: Number(companyId),
    });
    getUsers({ companyId: Number(companyId), admin: true });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [mappedData.length, globalSearchText, columnSearchText]);

  const getColumnSearchProps = (
    columnKey: SearchableColumnKey,
    label: string,
  ): Partial<ColumnType<ProductionRowData>> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${label}`}
          value={(selectedKeys[0] as string) || ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => {
            const value = String(selectedKeys[0] || "").trim();
            setColumnSearchText((prev) => ({ ...prev, [columnKey]: value }));
            confirm();
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              const value = String(selectedKeys[0] || "").trim();
              setColumnSearchText((prev) => ({ ...prev, [columnKey]: value }));
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              setSelectedKeys([]);
              setColumnSearchText((prev) => ({ ...prev, [columnKey]: "" }));
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    filteredValue: columnSearchText[columnKey]
      ? [columnSearchText[columnKey]]
      : null,
    onFilter: () => true,
  });

  const columns: ColumnType<ProductionRowData>[] = [
    {
      title: "Production ID",
      dataIndex: "productionId",
      key: "productionId",
      width: 180,
      fixed: "left",
      sorter: (a: any, b: any) =>
        String(a.productionId || "").localeCompare(
          String(b.productionId || ""),
        ),
      render: (value: string, record: any) => (
        <span
          className="link"
          onClick={() => {
            navigate(`/view_production?productionId=${record?.id}`);
          }}
        >
          {record?.production?.productionId || record?.productionId || ""}
        </span>
      ),
      ...getColumnSearchProps("productionId", "Production ID"),
    },
    {
      title: "Sales Order Number",
      dataIndex: "documentNumber",
      key: "documentNumber",
      width: 220,
      sorter: (a: any, b: any) =>
        String(a.documentNumber || "").localeCompare(
          String(b.documentNumber || ""),
        ),
      render: (value: string | null) => <span>{value || "N/A"}</span>,
      ...getColumnSearchProps("documentNumber", "Sales Order Number"),
    },
    {
      title: "BOM",
      dataIndex: "bomId",
      key: "bomId",
      width: 140,
      sorter: (a: any, b: any) =>
        (Number(a.bomId) || 0) - (Number(b.bomId) || 0),
      render: (value: number | null) => (
        <span>
          {bomDetailsData?.find((bom: any) => bom.id === value)?.bomId || "N/A"}
        </span>
      ),
      ...getColumnSearchProps("bomId", "BOM"),
    },
    {
      title: "Production Deadline",
      dataIndex: "productionEndDate",
      key: "productionEndDate",
      width: 200,
      sorter: (a: any, b: any) => {
        const aValue = a?.production?.productionEndDate || a?.productionEndDate || 0;
        const bValue = b?.production?.productionEndDate || b?.productionEndDate || 0;

        const aTime =
          typeof aValue === "string" && /^\d{2}-\d{2}-\d{4}$/.test(aValue)
            ? new Date(`${aValue.slice(6, 10)}-${aValue.slice(3, 5)}-${aValue.slice(0, 2)}T00:00:00Z`).getTime()
            : new Date(aValue).getTime();
        const bTime =
          typeof bValue === "string" && /^\d{2}-\d{2}-\d{4}$/.test(bValue)
            ? new Date(`${bValue.slice(6, 10)}-${bValue.slice(3, 5)}-${bValue.slice(0, 2)}T00:00:00Z`).getTime()
            : new Date(bValue).getTime();

        return (Number.isNaN(aTime) ? 0 : aTime) - (Number.isNaN(bTime) ? 0 : bTime);
      },
      render: (value: string | null) => <span>{value || "N/A"}</span>,
      ...getColumnSearchProps("productionEndDate", "Production Deadline"),
    },
    {
      title: "Production Start Date",
      dataIndex: "productionStartDate",
      key: "productionStartDate",
      width: 200,
      sorter: (a: any, b: any) =>
        new Date(
          a?.production?.productionStartDate || a?.productionStartDate || 0,
        ).getTime() -
        new Date(
          b?.production?.productionStartDate || b?.productionStartDate || 0,
        ).getTime(),
      render: (value: string, record: any) => (
        <span>
          {record?.production?.productionStartDate?.slice(0, 10) ||
            record?.productionStartDate?.slice(0, 10) ||
            ""}
        </span>
      ),
      ...getColumnSearchProps("productionStartDate", "Production Start Date"),
    },
    {
      title: "Production Type",
      dataIndex: "productionType",
      width: "180px",
      sorter: (a: { mto: any }, b: { mto: any }) => {
        const valA = a?.mto === 0 ? "MTS" : a?.mto === 1 ? "MTO" : "N/A";
        const valB = b?.mto === 0 ? "MTS" : b?.mto === 1 ? "MTO" : "N/A";
        return valA.localeCompare(valB);
      },
      render: (text: string, record: any) => (
        <span>
          {record?.mto === 0 ? "MTS" : record?.mto === 1 ? "MTO" : "N/A"}
        </span>
      ),
      ...getColumnSearchProps("productionType", "Production Type"),
    },
    {
      title: "Production Completion Date",
      dataIndex: "productionCompletionDate",
      key: "productionCompletionDate",
      width: 220,
      sorter: (a: any, b: any) =>
        new Date(
          a?.production?.productionCompletionDate ||
            a?.productionCompletionDate ||
            0,
        ).getTime() -
        new Date(
          b?.production?.productionCompletionDate ||
            b?.productionCompletionDate ||
            0,
        ).getTime(),
      render: (value: string, record: any) => (
        <span>
          {record?.production?.productionCompletionDate?.slice(0, 10) ||
            record?.productionCompletionDate?.slice(0, 10) ||
            ""}
        </span>
      ),
      ...getColumnSearchProps(
        "productionCompletionDate",
        "Production Completion Date",
      ),
    },
    {
      title: "Assigned To",
      dataIndex: "assignedTo",
      key: "assignedTo",
      width: 220,
      sorter: (a: any, b: any) =>
        String(usersMap[a.assignedTo] || a.assignedTo || "").localeCompare(
          String(usersMap[b.assignedTo] || b.assignedTo || ""),
        ),
      render: (value: number | null) => {
        if (value === null || value === undefined) return <span>N/A</span>;
        return <span>{usersMap[value] || "N/A"}</span>;
      },
      ...getColumnSearchProps("assignedTo", "Assigned To"),
    },
  ];

  const onDropdownChange = (value: string | number) => {
    if (handleDropdownChange) {
      handleDropdownChange(value);
    }
  };

  const breadcrumbItems = [
    { key: "dashboard", title: <Link to="/dashboard">Dashboard</Link> },
    { key: "productionExtra", title: breadcrumbTitle || "Production Extra" },
  ];

  return (
    <>
      <Loader loading={loading || bomDetailsLoading} />
      <Layout className="layout">
        <div className="flexBox mb-flexFlow" style={{ alignItems: "center" }}>
          <Breadcrumb className="breadcrumb" items={breadcrumbItems} />
          <Button
            type="primary"
            icon={<ArrowLeftOutlined style={{ color: "#ffffff" }} />}
            onClick={() => navigate("/dashboard")}
          >
            Back to Dashboard
          </Button>
        </div>

        <Content className="content-section">
          <div className="mb-10">
            <div className="flexBox mb-10">
              <Select
                style={{ width: 250 }}
                placeholder="Select option"
                value={selectedValue}
                onChange={onDropdownChange}
                options={normalizedOptions}
              />
              <Input
                placeholder="Search"
                value={globalSearchText}
                onChange={(e) => setGlobalSearchText(e.target.value)}
                style={{ width: 200, marginLeft: 12 }}
              />
            </div>

            {fullyFilteredData.length > 0 ? (
              <>
                <Table
                  columns={columns}
                  rowKey="key"
                  dataSource={fullyFilteredData}
                  bordered
                  pagination={{
                    position: ["bottomLeft"],
                    current: currentPage,
                    pageSize,
                    total: filteredTotal,
                    showSizeChanger: true,
                    onChange: (page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    },
                  }}
                  scroll={{ x: "100%", y: "calc(100vh - 320px)" }}
                />
              </>
            ) : (
              <div className="noData">
                <img src={noResult} alt="productionExtraNoData" />
                <p className="mt-20">No records found</p>
              </div>
            )}
          </div>
        </Content>
      </Layout>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  companyId: state.api.login.data.companyId,
  usersData: state.api.getUsers?.data,
  bomDetailsData: state.api?.getBOMDetails?.data,
  bomDetailsLoading: state.api?.getBOMDetails?.loading,
});

const mapDispatchToProps = (dispatch: any) => ({
  getBOMDetails: (payload: any) => dispatch(getBOMDetails(payload)),
  getUsers: (payload: any) => dispatch(getUsers(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductionExtra);
